import { 
  User, 
  CallLog, 
  AudioSession, 
  Recording, 
  CallTransfer, 
  PhoneNumber,
  ApiResponse,
  PaginatedResponse,
  FilterParams
} from 'types';
import { generateMockData } from './mockData';

// Helper function for pagination
function paginate<T>(data: T[], page: number, perPage: number): PaginatedResponse<T> {
  const start = (page - 1) * perPage;
  const end = start + perPage;
  
  return {
    data: data.slice(start, end),
    total: data.length,
    page,
    perPage,
    totalPages: Math.ceil(data.length / perPage)
  };
}

// Helper function for filtering call logs
function filterCallLogs(logs: CallLog[], filters: FilterParams): CallLog[] {
  let filtered = [...logs];

  if (filters.dateFrom) {
    filtered = filtered.filter(log => 
      new Date(log.start_time) >= new Date(filters.dateFrom!)
    );
  }

  if (filters.dateTo) {
    filtered = filtered.filter(log => 
      new Date(log.start_time) <= new Date(filters.dateTo!)
    );
  }

  if (filters.callType && filters.callType !== 'all') {
    filtered = filtered.filter(log => log.call_type === filters.callType);
  }

  if (filters.status && filters.status !== 'all') {
    filtered = filtered.filter(log => log.call_status === filters.status);
  }

  return filtered;
}

// Auth Mock Service
export const mockAuth = {
  async login(credentials: { username: string; password: string }): Promise<ApiResponse<any>> {
    await new Promise(resolve => setTimeout(resolve, 500));

    const users: Record<string, any> = {
      // Service Provider Users
      superadmin: {
        id: 1,
        username: 'superadmin',
        password: 'admin123',
        firstName: 'Super',
        lastName: 'Admin',
        email: 'super@cpass.com',
        organizations: [
          {
            id: 'org-service-1',
            name: 'CPass Platform',
            type: 'service',
            role: 'super_admin'
          }
        ]
      },
      serviceadmin: {
        id: 2,
        username: 'serviceadmin',
        password: 'admin123',
        firstName: 'Service',
        lastName: 'Admin',
        email: 'serviceadmin@cpass.com',
        organizations: [
          {
            id: 'org-service-1',
            name: 'CPass Platform',
            type: 'service',
            role: 'service_admin'
          }
        ]
      },
      serviceuser: {
        id: 3,
        username: 'serviceuser',
        password: 'user123',
        firstName: 'Service',
        lastName: 'User',
        email: 'serviceuser@cpass.com',
        organizations: [
          {
            id: 'org-service-1',
            name: 'CPass Platform',
            type: 'service',
            role: 'service_user'
          }
        ]
      },
      // Business/Tenant Users (single organization)
      operatoradmin1: {
        id: 4,
        username: 'operatoradmin1',
        password: 'admin123',
        firstName: 'John',
        lastName: 'Smith',
        email: 'john@acmecorp.com',
        organizations: [
          {
            id: 'org-biz-001',
            name: 'Acme Corporation',
            type: 'business',
            role: 'operator_admin',
            business_id: 'BIZ-001'
          }
        ]
      },
      operatoruser1: {
        id: 5,
        username: 'operatoruser1',
        password: 'user123',
        firstName: 'Jane',
        lastName: 'Doe',
        email: 'jane@acmecorp.com',
        organizations: [
          {
            id: 'org-biz-001',
            name: 'Acme Corporation',
            type: 'business',
            role: 'operator_user',
            business_id: 'BIZ-001'
          }
        ]
      },
      // Multi-organization user (works for both Acme and TechStart)
      multiorg: {
        id: 6,
        username: 'multiorg',
        password: 'admin123',
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah@example.com',
        organizations: [
          {
            id: 'org-biz-001',
            name: 'Acme Corporation',
            type: 'business',
            role: 'operator_admin',
            business_id: 'BIZ-001'
          },
          {
            id: 'org-biz-002',
            name: 'TechStart Inc',
            type: 'business',
            role: 'operator_user',
            business_id: 'BIZ-002'
          },
          {
            id: 'org-biz-003',
            name: 'Global Solutions Ltd',
            type: 'business',
            role: 'operator_admin',
            business_id: 'BIZ-003'
          }
        ]
      },
      // Service provider with multiple roles
      consultant: {
        id: 7,
        username: 'consultant',
        password: 'admin123',
        firstName: 'Mike',
        lastName: 'Wilson',
        email: 'mike@cpass.com',
        organizations: [
          {
            id: 'org-service-1',
            name: 'CPass Platform',
            type: 'service',
            role: 'service_admin'
          },
          {
            id: 'org-biz-002',
            name: 'TechStart Inc',
            type: 'business',
            role: 'operator_admin',
            business_id: 'BIZ-002'
          }
        ]
      }
    };

    const user = users[credentials.username];
    
    if (user && user.password === credentials.password) {
      const { password, ...userWithoutPassword } = user;
      return {
        success: true,
        data: {
          user: userWithoutPassword,
          token: `mock-jwt-token-${Date.now()}`,
          refreshToken: `mock-refresh-token-${Date.now()}`
        }
      };
    }

    return {
      success: false,
      error: 'Invalid username or password'
    };
  },

  async logout(): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 200));
  }
};

// Calls Mock Service
export const mockCalls = {
  data: generateMockData(),
  
  async getCallLogs(params?: FilterParams): Promise<ApiResponse<PaginatedResponse<CallLog>>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let filtered = [...this.data.callLogs];
    
    if (params) {
      filtered = filterCallLogs(filtered, params);
    }
    
    const paginated = paginate(
      filtered, 
      params?.page || 1, 
      params?.perPage || 10
    );
    
    return {
      success: true,
      data: paginated
    };
  },

  async getCallDetail(id: number): Promise<ApiResponse<CallLog>> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const call = this.data.callLogs.find(c => c.id === id);
    
    if (call) {
      return {
        success: true,
        data: call
      };
    }
    
    return {
      success: false,
      error: 'Call not found'
    };
  }
};

// Sessions Mock Service
export const mockSessions = {
  data: generateMockData(),

  async getAudioSessions(params?: any): Promise<ApiResponse<PaginatedResponse<AudioSession>>> {
    await new Promise(resolve => setTimeout(resolve, 300));

    let filtered = [...this.data.audioSessions];

    // Filter by date range
    if (params?.dateFrom) {
      filtered = filtered.filter(session =>
        new Date(session.connection_start) >= new Date(params.dateFrom)
      );
    }

    if (params?.dateTo) {
      filtered = filtered.filter(session =>
        new Date(session.connection_start) <= new Date(params.dateTo)
      );
    }

    const paginated = paginate(
      filtered,
      params?.page || 1,
      params?.perPage || 10
    );

    return {
      success: true,
      data: paginated
    };
  },

  async getSessionDetail(id: number): Promise<ApiResponse<AudioSession>> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const session = this.data.audioSessions.find(s => s.id === id);
    
    if (session) {
      return {
        success: true,
        data: session
      };
    }
    
    return {
      success: false,
      error: 'Session not found'
    };
  }
};

// Recordings Mock Service
export const mockRecordings = {
  data: generateMockData(),
  
  async getRecordings(params?: any): Promise<ApiResponse<PaginatedResponse<Recording>>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const paginated = paginate(
      this.data.recordings, 
      params?.page || 1, 
      params?.perPage || 10
    );
    
    return {
      success: true,
      data: paginated
    };
  }
};

// Transfers Mock Service
export const mockTransfers = {
  data: generateMockData(),

  async getTransfers(params?: FilterParams): Promise<ApiResponse<PaginatedResponse<CallTransfer>>> {
    await new Promise(resolve => setTimeout(resolve, 300));

    let filtered = [...this.data.callTransfers];

    if (params?.status && params.status !== 'all') {
      filtered = filtered.filter(t => t.transferStatus === params.status);
    }

    // Filter by date range
    if (params?.dateFrom) {
      filtered = filtered.filter(transfer =>
        new Date(transfer.transferTime ?? 0) >= new Date(params.dateFrom!)
      );
    }

    if (params?.dateTo) {
      filtered = filtered.filter(transfer =>
        new Date(transfer.transferTime ?? 0) <= new Date(params.dateTo!)
      );
    }

    const paginated = paginate(
      filtered,
      params?.page || 1,
      params?.perPage || 10
    );

    return {
      success: true,
      data: paginated
    };
  }
};

// Phones Mock Service
export const mockPhones = {
  data: generateMockData(),
  nextId: 100,
  
  async getPhoneNumbers(params?: any): Promise<ApiResponse<PaginatedResponse<PhoneNumber>>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const paginated = paginate(
      this.data.phoneNumbers, 
      params?.page || 1, 
      params?.perPage || 10
    );
    
    return {
      success: true,
      data: paginated
    };
  },

  async createPhoneNumber(data: Omit<PhoneNumber, 'id'>): Promise<ApiResponse<PhoneNumber>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newPhone: PhoneNumber = {
      ...data,
      id: this.nextId++
    };
    
    this.data.phoneNumbers.push(newPhone);
    
    return {
      success: true,
      data: newPhone
    };
  },

  async updatePhoneNumber(id: number, data: Partial<PhoneNumber>): Promise<ApiResponse<PhoneNumber>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = this.data.phoneNumbers.findIndex(p => p.id === id);
    
    if (index !== -1) {
      this.data.phoneNumbers[index] = {
        ...this.data.phoneNumbers[index],
        ...data
      };
      
      return {
        success: true,
        data: this.data.phoneNumbers[index]
      };
    }
    
    return {
      success: false,
      error: 'Phone number not found'
    };
  },

  async deletePhoneNumber(id: number): Promise<ApiResponse<void>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = this.data.phoneNumbers.findIndex(p => p.id === id);
    
    if (index !== -1) {
      this.data.phoneNumbers.splice(index, 1);
      
      return {
        success: true
      };
    }
    
    return {
      success: false,
      error: 'Phone number not found'
    };
  }
};

// Users Mock Service
export const mockUsers = {
  data: generateMockData(),
  nextId: 100,
  
  async getUsers(params?: any): Promise<ApiResponse<PaginatedResponse<User>>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const paginated = paginate(
      this.data.users, 
      params?.page || 1, 
      params?.perPage || 10
    );
    
    return {
      success: true,
      data: paginated
    };
  },

  async createUser(data: Omit<User, 'id'>): Promise<ApiResponse<User>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const newUser: User = {
      ...data,
      id: this.nextId++
    };
    
    this.data.users.push(newUser);
    
    return {
      success: true,
      data: newUser
    };
  },

  async updateUser(id: number, data: Partial<User>): Promise<ApiResponse<User>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = this.data.users.findIndex(u => u.id === id);
    
    if (index !== -1) {
      this.data.users[index] = {
        ...this.data.users[index],
        ...data
      };
      
      if (data.password === '') {
        delete this.data.users[index].password;
      }
      
      return {
        success: true,
        data: this.data.users[index]
      };
    }
    
    return {
      success: false,
      error: 'User not found'
    };
  },

  async deleteUser(id: number): Promise<ApiResponse<void>> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = this.data.users.findIndex(u => u.id === id);
    
    if (index !== -1) {
      this.data.users.splice(index, 1);
      
      return {
        success: true
      };
    }
    
    return {
      success: false,
      error: 'User not found'
    };
  }
};

// Organizations Mock Service
export const mockOrganizations = {
  businesses: [
    {
      id: 'org-biz-001',
      name: 'Acme Corporation',
      type: 'BUSINESS',
      businessId: 'BIZ-001',
      parentOrganizationId: 'org-service-1',
      description: 'Leading provider of enterprise solutions',
      active: true,
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-15T10:00:00Z'
    },
    {
      id: 'org-biz-002',
      name: 'TechStart Inc',
      type: 'BUSINESS',
      businessId: 'BIZ-002',
      parentOrganizationId: 'org-service-1',
      description: 'Innovative startup in tech sector',
      active: true,
      createdAt: '2024-02-01T14:30:00Z',
      updatedAt: '2024-02-01T14:30:00Z'
    },
    {
      id: 'org-biz-003',
      name: 'Global Solutions Ltd',
      type: 'BUSINESS',
      businessId: 'BIZ-003',
      parentOrganizationId: 'org-service-1',
      description: 'International consulting firm',
      active: true,
      createdAt: '2024-02-10T09:15:00Z',
      updatedAt: '2024-02-10T09:15:00Z'
    },
    {
      id: 'org-biz-004',
      name: 'Retail Pro Systems',
      type: 'BUSINESS',
      businessId: 'BIZ-004',
      parentOrganizationId: 'org-service-1',
      description: 'Retail management software provider',
      active: true,
      createdAt: '2024-02-20T11:00:00Z',
      updatedAt: '2024-02-20T11:00:00Z'
    },
    {
      id: 'org-biz-005',
      name: 'HealthCare Connect',
      type: 'BUSINESS',
      businessId: 'BIZ-005',
      parentOrganizationId: 'org-service-1',
      description: 'Healthcare communication platform',
      active: true,
      createdAt: '2024-03-01T08:45:00Z',
      updatedAt: '2024-03-01T08:45:00Z'
    }
  ],
  services: [
    {
      id: 'org-service-1',
      name: 'CPass Platform',
      type: 'SERVICE',
      businessId: 'SVC-001',
      description: 'Primary service provider platform',
      active: true,
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-01T00:00:00Z'
    }
  ],
  nextId: 6,

  async getAllBusinesses(): Promise<ApiResponse<any[]>> {
    await new Promise(resolve => setTimeout(resolve, 300));

    return {
      success: true,
      data: this.businesses
    };
  },

  async getBusinessesByServiceProvider(serviceId: string): Promise<ApiResponse<any[]>> {
    await new Promise(resolve => setTimeout(resolve, 300));

    const filtered = this.businesses.filter(b => b.parentOrganizationId === serviceId);

    return {
      success: true,
      data: filtered
    };
  },

  async getServiceProviders(): Promise<ApiResponse<any[]>> {
    await new Promise(resolve => setTimeout(resolve, 300));

    return {
      success: true,
      data: this.services
    };
  },

  async getOrganizationById(id: string): Promise<ApiResponse<any>> {
    await new Promise(resolve => setTimeout(resolve, 200));

    const org = [...this.businesses, ...this.services].find(o => o.id === id);

    if (org) {
      return {
        success: true,
        data: org
      };
    }

    return {
      success: false,
      error: 'Organization not found'
    };
  },

  async createBusiness(data: any): Promise<ApiResponse<any>> {
    await new Promise(resolve => setTimeout(resolve, 500));

    // Validate service provider exists
    const serviceExists = this.services.find(s => s.id === data.serviceProviderId);
    if (!serviceExists) {
      return {
        success: false,
        message: 'Service provider not found'
      };
    }

    // Check if business ID is unique
    const businessIdExists = this.businesses.find(b => b.businessId === data.businessId);
    if (businessIdExists) {
      return {
        success: false,
        message: 'Business ID already exists'
      };
    }

    const newBusiness = {
      id: `org-biz-${String(this.nextId++).padStart(3, '0')}`,
      name: data.businessName,
      type: 'BUSINESS',
      businessId: data.businessId,
      parentOrganizationId: data.serviceProviderId,
      description: data.businessDescription || '',
      active: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    this.businesses.push(newBusiness);

    return {
      success: true,
      message: 'Business created successfully',
      data: newBusiness
    };
  }
};